/**
 * "What I read" — the code-written tail of a Lane V2 answer, split off the answer body (leg `gui-what-i-read`).
 *
 * MSA appends one machine-written block as the LAST thing of an answer (`shared/evidence_section.py`):
 *
 *     ...answer...
 *
 *     ## Data results      (optional; folded into the same footer disclosure as the queries it lists, `Queries (M)`)
 *
 *     ## Evidence
 *
 *     Sources read: 7
 *
 *     - [Source code: AccountTriggerHandler](artifact://<id>)
 *     - Metadata search for "contract renewal"
 *
 * An answer that read nothing carries `Sources read: 0. This answer was written without reading the org.`
 *
 * Above Evidence, in this order, may sit `## Data results` (queries, raw SOQL, masked sample tables, result links) and `## Org facts`
 * (one line per object card). The Evidence section may carry `Coverage: listed L, read R` (leg `w1-coverage`) under `Sources read: N`.
 *
 * Recognising the heading is a structural parse of the product's OWN output (the same cut order and line grammar as MSA's `split_tail`; the GUI is stricter: it also checks the Evidence body is code-written, and splits on \n only, not every Unicode line break), never a
 * reading of what a human or the model meant. Stored answers from before the `Sources read:` line have only the list lines.
 */

const HEADING = "## Evidence";
const DATA_HEADING = "## Data results";
const ORG_HEADING = "## Org facts";
/**
 * The line starts `data_block` writes (MSA `evidence_section.DATA_LINE_STARTS`, mirrored exactly): query lines, the raw SOQL, the
 * `- and K more queries` cap, the sample caption, table rows, the saved-result sentence, the untyped-fields note and the export
 * sentence. Anything else above Evidence is the answer's own.
 */
const SAMPLE_PREFIX = "Sample of ";
const FULL_RESULT_PREFIX = "The full result ";
const UNTYPED_PREFIX = "Field types could not be read ";
const EXPORT_SENTENCE =
  "For an audit-grade export, use a Salesforce report or the org's Data Export, which apply the org's own permissions and audit trail.";
const DATA_LINE_STARTS = ["- Query ", "- and ", "  `", SAMPLE_PREFIX, "|", FULL_RESULT_PREFIX, UNTYPED_PREFIX, EXPORT_SENTENCE];
/** Every `## Org facts` line `org_line` writes carries this (or is the `- and K more objects` cap). */
const ORG_SOURCE_PREFIX = "Source: org describe";
/** Leg `w1-coverage`: `Coverage: listed L, read R`, right after `Sources read: N`. */
const COVERAGE_RE = /^Coverage: listed (\d{1,9}), read (\d{1,9})$/;
const SOURCES_PREFIX = "Sources read:";
/** `- and K more` (the cap line of the list). */
const MORE_LINE = /^- and (\d{1,6}) more$/;
/** The three line shapes of a tail stored before the `Sources read:` line: `- \`id\``, `- [name](artifact://id)`, `- and K more`. */
const OLD_TAIL_LINE = /^(- `[^`\n]+`|- \[[^\]\n]+\]\(artifact:\/\/[^)\s]+\)|- and \d{1,6} more)$/;

export interface SourcesTail {
  /** The answer without its code-written tail (`## Org facts`, `## Data results` and `## Evidence` all leave the body). */
  body: string;
  /** The `## Data results` lines (queries and their raw SOQL), as markdown without the heading; "" when the tail carried none. */
  queriesMarkdown: string;
  /** How many queries the section lists (its `- Query N` lines plus the `- and K more queries` cap line); 0 when none. */
  queryCount: number;
  /** The list lines of the tail (`- ...`), as markdown, one per read; "" when the tail lists none. */
  listMarkdown: string;
  /** The count the tail states (`Sources read: N`), else the number of list lines. */
  readCount: number;
  /** True when the tail carried its own `Sources read: N` line. */
  stated: boolean;
  /** The `## Org facts` lines as markdown without the heading; "" when the tail carried none. */
  orgFactsMarkdown: string;
  /** The measured `Coverage: listed L, read R` line, or null when the tail has none. */
  coverage: { listed: number; read: number } | null;
}

/** The count in a `Sources read: N` line (also the zero line's `Sources read: 0. ...`), or null. */
function statedCount(line: string): number | null {
  const bare = line.trim();
  if (!bare.startsWith(SOURCES_PREFIX)) return null;
  const m = /^\s*(\d{1,6})\b/.exec(bare.slice(SOURCES_PREFIX.length));
  return m ? Number(m[1]) : null;
}

/** `markdown` split at the LAST `\n\n## Evidence\n`; null when the answer has no such tail. */
export function splitSourcesTail(markdown: string | null | undefined): SourcesTail | null {
  if (!markdown) return null;
  const at = markdown.lastIndexOf(`\n\n${HEADING}\n`);
  if (at < 0) return null;
  const tailLines = markdown.slice(at + 2 + HEADING.length).split("\n");
  let stated: number | null = null;
  let coverage: { listed: number; read: number } | null = null;
  const list: string[] = [];
  let listed = 0;
  let allOldShapes = true;
  for (const line of tailLines) {
    if (!line.trim()) continue;
    const cov = COVERAGE_RE.exec(line.trim());
    if (cov) {
      coverage ??= { listed: Number(cov[1]), read: Number(cov[2]) };
      continue;
    }
    const n = statedCount(line);
    if (n !== null) {
      if (stated === null) stated = n;
      continue;
    }
    const more = MORE_LINE.exec(line.trim());
    if (more) listed += Number(more[1]);
    else if (line.trimStart().startsWith("- ")) listed += 1;
    if (!OLD_TAIL_LINE.test(line.trim())) allOldShapes = false;
    if (line.trimStart().startsWith("- ")) list.push(line.trimEnd());
  }
  // Only a tail the code wrote collapses: it states `Sources read:` (always, zero included), or (stored before that line existed)
  // every line is one of the shapes `evidence_section.render` writes. A model-written `## Evidence` stays ordinary markdown.
  if (stated === null && !allOldShapes) return null;
  // Same order as MSA's `split_tail`: Data results directly above Evidence, then Org facts directly above whichever of those starts the tail.
  const data = splitDataResults(markdown, at);
  const org = splitOrgFacts(markdown, data?.start ?? at);
  return {
    body: markdown.slice(0, org?.start ?? data?.start ?? at),
    queriesMarkdown: data?.markdown ?? "",
    queryCount: data?.count ?? 0,
    listMarkdown: list.join("\n"),
    readCount: stated ?? listed,
    stated: stated !== null,
    orgFactsMarkdown: org?.markdown ?? "",
    coverage,
  };
}

/** The lines of `markdown` between `from` (just past a heading) and `to`, trimmed of surrounding blank lines. */
function sectionLines(markdown: string, from: number, to: number): string[] {
  const lines = markdown.slice(from, to).split("\n").map((l) => l.trimEnd());
  while (lines.length && !lines[0]) lines.shift();
  while (lines.length && !lines[lines.length - 1]) lines.pop();
  return lines;
}

/**
 * The code's `## Data results` section sitting DIRECTLY above `tailAt` (the Evidence tail): found only when every line between its
 * heading and the tail is one `data_block` writes (MSA's `split_tail` guard), so a model-written heading of the same name stays
 * ordinary answer text. Blank lines and sample tables are kept in the section's markdown.
 */
function splitDataResults(markdown: string, tailAt: number): { start: number; markdown: string; count: number } | null {
  const at = markdown.lastIndexOf(`\n\n${DATA_HEADING}\n`, tailAt);
  if (at < 0) return null;
  const from = at + 2 + DATA_HEADING.length;
  const raw = markdown.slice(from, tailAt).split("\n");
  if (!raw.every((l) => !l.trim() || DATA_LINE_STARTS.some((p) => l.startsWith(p)))) return null;
  const lines = sectionLines(markdown, from, tailAt);
  if (lines.length === 0) return null;
  let count = 0;
  for (const l of lines) {
    const more = /^- and (\d{1,6}) more queries$/.exec(l);
    if (more) count += Number(more[1]);
    else if (l.startsWith("- Query ")) count += 1;
  }
  return { start: at, markdown: lines.join("\n"), count };
}

/** The code's `## Org facts` section directly above `tailAt`: every line is an `org_line` (`- ... Source: org describe...`) or the cap line. */
function splitOrgFacts(markdown: string, tailAt: number): { start: number; markdown: string } | null {
  const at = markdown.lastIndexOf(`\n\n${ORG_HEADING}\n`, tailAt);
  if (at < 0) return null;
  const from = at + 2 + ORG_HEADING.length;
  const raw = markdown.slice(from, tailAt).split("\n");
  if (!raw.every((l) => !l.trim() || (l.startsWith("- ") && (l.includes(ORG_SOURCE_PREFIX) || l.startsWith("- and "))))) return null;
  const lines = sectionLines(markdown, from, tailAt);
  return lines.length === 0 ? null : { start: at, markdown: lines.join("\n") };
}

/**
 * True when the answer's tail says it read nothing from the org: the tail states `Sources read: 0` (or, stored before that line, lists
 * nothing and the turn made no calls) and no query is listed. The completion row shows this as a muted tag instead of its own line.
 */
export function readNothing(tail: SourcesTail | null, toolCalls: number | null | undefined): boolean {
  return !!tail && tail.queryCount === 0 && sourcesReadCount(tail, toolCalls) === 0;
}

/**
 * The N the summary shows: what the tail states (`Sources read: N`, one owner: the same call log that wrote the list under it).
 * A tail stored before that line falls back to the turn's R5 `trace.tool_calls`, then to the list-line count.
 */
export function sourcesReadCount(tail: SourcesTail, toolCalls: number | null | undefined): number {
  if (tail.stated) return tail.readCount;
  return typeof toolCalls === "number" && toolCalls >= 0 ? toolCalls : tail.readCount;
}

/**
 * MSA leg `notion-e2e`: one listed read about a CONNECTED external tool (Notion, Jira, GitHub): the backend's own noun
 * ("Connected tool search for …", "Connected tool record"), linked or plain, or the raw `connectors.*` tool name an answer
 * stored before that noun carries. An "Attachment" line (the saved rest of a long record) and the "and K more" cap are neutral.
 */
const CONNECTED_READ_LINE = /^- \[?(Connected tool (search|record)\b|connectors\.[a-z_]+)/;
const NEUTRAL_READ_LINE = /^(- \[?Attachment\b|- and \d{1,6} more$)/;

/**
 * True when every read the tail lists is a connected-tool read (at least one) and no org query is listed: the answer is about a
 * page, issue or record in a connected tool, so Salesforce-shaped follow-ups ("Show callers", "Show tests") would not fit it.
 */
export function readsOnlyConnectedTools(tail: SourcesTail | null): boolean {
  if (!tail || tail.queryCount > 0) return false;
  const lines = tail.listMarkdown.split("\n").map((l) => l.trim()).filter((l) => l.startsWith("- "));
  const reads = lines.filter((l) => !NEUTRAL_READ_LINE.test(l));
  return reads.length > 0 && reads.every((l) => CONNECTED_READ_LINE.test(l));
}
